import React, { useCallback, useRef } from 'react';
import { isNil } from 'lodash';

import { ChatInputV2 } from '~/components/AiChat/components/ChatInputV2';

import { CONVERSATION_TEXTS } from '../../conversation/texts';

import type { ComposerSend } from './useComposerSend';

/** The composer's text and its send, which the conversation's foot holds: the queue draws the message on its way. */
type ComposerProps = Omit<ComposerSend, 'inFlight'>;

/**
 * The field at the foot of the log the handler writes to Clive in. Always usable: whatever Clive is doing,
 * a message waits for it. Nothing under the field: a message on its way waits in the queue, and one that did not
 * go is back in the field, the box saying so. The Clive chat's input without its + menu, since a message here has
 * no chat to add documents or prompts to.
 */
const Composer: React.FC<ComposerProps> = ({ value, setValue, isSending, error, submit }) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Sent with the button too, the handler stays in the field, read-only until the server answers.
  const submitFromField = useCallback(() => {
    inputRef.current?.focus();

    return submit();
  }, [submit]);

  return (
    // In a block, as the Clive chat sets it: a flex item itself, its `self-center` would shrink it to its content.
    <div>
      <ChatInputV2
        value={value}
        onChange={setValue}
        onSubmit={submitFromField}
        readOnly={isSending}
        invalid={!isNil(error)}
        invalidLabel={CONVERSATION_TEXTS.NOT_SENT}
        invalidDescription={error?.reason}
        placeholder={CONVERSATION_TEXTS.COMPOSER_PLACEHOLDER}
        inputRef={inputRef}
        showAttachMenu={false}
        density="compact"
      />
    </div>
  );
};

export default Composer;
